import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, FileText, Mic, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import TopBar from "@/components/TopBar";

const STATUS = {
  done: { label: "Update ready", cls: "bg-emerald-100 text-emerald-700" },
  transcribed: { label: "Transcribed", cls: "bg-amber-100 text-amber-700" },
  recorded: { label: "Recorded", cls: "bg-[#F3EFEA] text-[#8A6D5C]" },
  empty: { label: "No audio", cls: "bg-[#F3EFEA] text-[#A8968E]" },
};

function statusOf(s) {
  if (s.generated_message) return STATUS.done;
  if (s.transcript) return STATUS.transcribed;
  if (s.audio_file_id || (s.audio_file_ids && s.audio_file_ids.length > 0)) return STATUS.recorded;
  return STATUS.empty;
}

function fmt(iso) {
  return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        return api.get("/sessions");
      })
      .then((r) => { setSessions(r.data); setLoaded(true); })
      .catch(() => navigate("/login"));
  }, [navigate]);

  if (!user) return null;

  return (
    <main data-testid="dashboard-page" className="min-h-screen bg-[#FDF3EC]">
      <TopBar user={user} />

      <div className="mx-auto w-full max-w-4xl px-5 py-10 md:px-8">
        {/* Header row */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <motion.h1
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              data-testid="dashboard-title"
              className="font-display text-3xl font-bold text-[#1A1918] md:text-4xl"
            >
              Namaste, {user.name.split(" ")[0]}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.08 }}
              className="mt-1 text-sm text-[#8A6D5C]"
            >
              Your recorded class sessions
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.15 }}
          >
            <Link
              to="/sessions/new"
              data-testid="new-session-btn"
              className="group inline-flex items-center gap-2 rounded-2xl bg-[#E8542A] px-5 py-2.5 text-sm font-bold text-white transition-all duration-200 hover:bg-[#C94520] hover:gap-2.5"
            >
              <Plus className="h-4 w-4" />
              <span>New session</span>
            </Link>
          </motion.div>
        </div>

        {/* Stats row */}
        {loaded && sessions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-6 grid grid-cols-3 gap-3"
          >
            {[
              { label: "Total sessions", value: sessions.length, icon: FileText },
              { label: "Transcribed", value: sessions.filter(s => s.transcript).length, icon: Mic },
              { label: "Updates sent", value: sessions.filter(s => s.generated_message).length, icon: Sparkles },
            ].map(({ label, value, icon: Icon }) => (
              <div key={label} className="rounded-2xl border border-black/5 bg-white p-4">
                <div className="flex items-center gap-2 text-[#8A6D5C]">
                  <Icon className="h-3.5 w-3.5" />
                  <p className="text-xs font-medium">{label}</p>
                </div>
                <p className="font-display mt-1.5 text-3xl font-bold text-[#1A1918]">{value}</p>
              </div>
            ))}
          </motion.div>
        )}

        {/* Sessions list */}
        <div className="mt-8 space-y-2.5">
          {loaded && sessions.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              data-testid="empty-sessions"
              className="flex flex-col items-center gap-3 rounded-3xl border-2 border-dashed border-[#E8542A]/20 bg-white/60 py-16 text-center"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#E8542A]/10">
                <Mic className="h-5 w-5 text-[#E8542A]" />
              </div>
              <div>
                <p className="font-semibold text-[#1A1918]">No sessions yet</p>
                <p className="mt-0.5 text-sm text-[#8A6D5C]">Record your first class to get started</p>
              </div>
              <Link
                to="/sessions/new"
                className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#E8542A] px-5 py-2 text-sm font-bold text-white hover:bg-[#C94520]"
              >
                <Plus className="h-3.5 w-3.5" /> Record a class
              </Link>
            </motion.div>
          )}

          {sessions.map((s, i) => {
            const st = statusOf(s);
            return (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.25 + i * 0.05 }}
              >
                <Link
                  to={`/sessions/${s.id}`}
                  data-testid={`session-card-${s.id}`}
                  className="flex items-center justify-between rounded-2xl border border-black/5 bg-white p-5 transition-all duration-200 hover:border-[#E8542A]/30 hover:shadow-sm"
                >
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-[#1A1918]">
                      {s.subject || "Class"}{s.standard ? ` — Std ${s.standard}` : ""}{s.division ? ` ${s.division}` : ""}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-[#8A6D5C]">
                      {s.school_name} · {fmt(s.created_at)}
                    </p>
                  </div>
                  <span
                    data-testid={`session-status-${s.id}`}
                    className={`ml-4 flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${st.cls}`}
                  >
                    <FileText className="h-3 w-3" />
                    {st.label}
                  </span>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>
    </main>
  );
}
